import React, { useEffect, useState } from "react";
import { contentHeader } from "../utils/header";
import Select from "react-select";
import axios from "axios";
import AnnualEvictionTrends from "../components/charts/AnnualEvictionTrends";
import EvictionByType from "../components/charts/EvictionByType";
import EvictionByMonth from "../components/charts/EvictionByMonth";
import IncidenceByEntity from "../components/charts/IncidenceByEntity";
import ProtectionRisksChart from "../components/charts/ProtectionRiskChart";
import InfrastructureBarChart from "../components/charts/InfrastructureChart";
import { ArrowUp, ArrowDown, Minus, Users, Home } from "lucide-react";
type Props = {};
type EvictionDataType = {
  labels: string[];
  values: number[];
};

interface DataRowProps {
  label: string;
  icon: React.ReactNode;
  current?: number;
  previous?: number;
  pctChange: (c?: number, p?: number) => string;
  fmt: (n?: number) => string;
}
type RegionData = {
  total_households?: number;
  total_individuals?: number;
};

type EvictionDataState = {
  byType: EvictionDataType;
  byEntity: EvictionDataType;
  byAgreementType: EvictionDataType;
  byTenureType: EvictionDataType;
  byPropertyType: EvictionDataType;
};

type FileItem = {
  id: number;
  thumbnail_url: string;
  title?: string; // optional, in case you want alt text
};

interface EvictionData {
  city: string;
  month: string;
  numberOfEvictions: number;
}

interface CityOption {
  label: string;
  value: string;
}

interface CommunityData {
  community_centres: number;
  quranic_schools: number;
  water_kiosks: number;
  small_shops: number;
  sanitation_facilities: number;
  markets: number;
  community_health_centres: number;
  other_community_facility_count: number;
}

interface HumanitarianData {
  schools: number;
  health_centres: number;
  distribution_centres: number;
  protection_counselling: number;
  solar_lights: number;
  water_points: number;
  latrines: number;
  nutrition_centres: number;
  psn_houses: number;
  other_humanitarian_facility_count: number;
}

const Evictions = (props: Props) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [tab, setTab] = useState<number>(1);
  const [data, setData] = useState<Record<string, RegionData>>({});
  const [previousData, setPreviousData] = useState<Record<string, RegionData>>({});
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, index) =>
    (currentYear - index).toString()
  );
  const [partnerLogos, setPartnerLogos] = useState<FileItem[]>([]);
  const [chartData, setChartData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [communityData, setCommunityData] = useState<CommunityData | null>(null);
  const [humanitarianData, setHumanitarianData] = useState<HumanitarianData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString()
  );
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [evictionData, setEvictionData] = useState<EvictionDataState>({
    byType: { labels: [], values: [] },
    byEntity: { labels: [], values: [] },
    byAgreementType: { labels: [], values: [] },
    byTenureType: { labels: [], values: [] },
    byPropertyType: { labels: [], values: [] },
  });

  // Fetch cities from backend
  const fetchCities = async () => {
    try {
      const response = await axios.get<CityOption[]>(
        `${BASE_URL}/cities/`
      );
      setCities(response.data);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/prod_eviction_stats/`);
        // Assuming API returns { availableYears: [2017, 2018, ...] }
        setAvailableYears(res.data.availableYears ?? []);
        let apiYears: number[] = res.data.availableYears ?? [];
        apiYears = apiYears.sort((a, b) => b - a);
      } catch (error) {
        console.error("Error fetching available years:", error);
      }
    };

    fetchYears();
  }, []);

  const DataRowProp = ({
    label,
    icon,
    current,
    previous,
    pctChange,
    fmt,
  }: DataRowProps) => {
    const changeStr = pctChange(current, previous);
    const change = parseFloat(changeStr);

    // Determine if higher numbers are positive (for Protected) or negative (for Evicted)
    const isProtected = label.toLowerCase().includes("protected");
    const isEvicted = label.toLowerCase().includes("evicted");

    // For protected → up = green, down = red
    // For evicted → down = green, up = red
    const isIncrease = change > 0;
    const isDecrease = change < 0;

    const isPositive =
      (isProtected && isIncrease) || (isEvicted && isDecrease);
    const isNegative =
      (isProtected && isDecrease) || (isEvicted && isIncrease);

    const color = isPositive
      ? "#16a34a" // green
      : isNegative
        ? "#dc2626" // red
        : "#6b7280"; // gray

    const iconBg = isPositive
      ? "#16a34a20"
      : isNegative
        ? "#dc262620"
        : "#6b728020";

    return (
      <div className="flex flex-col items-start justify-between rounded-2xl bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-full"
            style={{ backgroundColor: iconBg }}
          >
            {icon}
          </div>
          <p className="text-sm font-medium text-[#1f2937]">{label}</p>
        </div>

        <div className="mt-2 flex items-baseline gap-2">
          <p className="text-2xl font-bold text-[#1f2937]">{fmt(current)}</p>
          <span style={{ color }} className="text-sm flex items-center gap-1 font-semibold">
            {isIncrease ? <ArrowUp size={14} /> : isDecrease ? <ArrowDown size={14} /> : <Minus size={14} />}
            {Math.abs(change).toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  const evictionTypes = [
    { key: "byType", label: "Eviction Type", endpoint: "evictions-by-evictions-type" },
    { key: "byEntity", label: "Entity Type", endpoint: "evictions-by-entity-type" },
    { key: "byAgreementType", label: "Agreement Type", endpoint: "evictions-by-agreement-type" },
    { key: "byTenureType", label: "Tenure Type", endpoint: "evictions-by-tenure-type" },
    { key: "byPropertyType", label: "Property Type", endpoint: "evictions-by-property-type" },
  ] as const;

  const regionEndpoints = [
    { key: "protectedHouseholds", label: "Protected Households", endpoint: "preventiondata/protected-households-by-region" },
    { key: "protectedIndividuals", label: "Protected Individuals", endpoint: "preventiondata/protected-individuals-by-region" },
    { key: "evictedHouseholds", label: "Evicted Households", endpoint: "evictiondata/evicted-households-by-region" },
    { key: "evictedIndividuals", label: "Evicted Individuals", endpoint: "evictiondata/evicted-individuals-by-region" },
  ] as const;

  const fetchEviction = async (year: string, city: string | null) => {
    try {
      setIsLoading(true);
      const queryParams = `year=${year}${city ? `&city=${city}` : ""}`;
      const headers = contentHeader();

      // Fetch all prevention types in parallel
      const responses = await Promise.all(
        evictionTypes.map(({ endpoint }) =>
          axios
            .get<{ labels?: string[]; values?: number[] }>(
              `${BASE_URL}/evictiondata/${endpoint}/?${queryParams}`,
              { headers }
            )
            .catch((error) => {
              console.error(`Error fetching ${endpoint}:`, error);
              return { data: { labels: [], values: [] } }; // Return empty structure on error
            })
        )
      );

      // Structure the fetched data properly
      const evictionData = evictionTypes.reduce(
        (acc, { key }, index) => ({
          ...acc,
          [key]: responses[index]?.data ?? { labels: [], values: [] },
        }),
        {} as EvictionDataState
      );

      setEvictionData(evictionData);

      // Generate chart data dynamically
      const chartData = evictionTypes.reduce((acc, { key }) => {
        const data = evictionData[key];

        if (!data?.labels?.length || !data?.values?.length) {
          return acc; // Skip if there's no data
        }

        acc[key] = {
          labels: data.labels,
          datasets: [
            {
              label: evictionTypes.find((t) => t.key === key)?.label || key,
              data: data.values,
              backgroundColor: "rgba(54, 162, 235, 0.5)",
            },
          ],
        };

        return acc;
      }, {} as Record<string, any>);

      setChartData(chartData);
    } catch (error) {
      console.error("Error fetching prevention data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRegionData = async (year: string, city: string | null) => {
    setLoading(true);
    const prevYear = (parseInt(year) - 1).toString();
    const queryParams = `year=${year}${city ? `&city=${city}` : ""}`;
    const prevQueryParams = `year=${prevYear}${city ? `&city=${city}` : ""}`;
    const headers = { "Content-Type": "application/json" };

    const responses = await Promise.all(
      regionEndpoints.flatMap(({ endpoint }) => [
        axios
          .get<RegionData>(`${BASE_URL}/${endpoint}/?${queryParams}`, { headers })
          .catch(() => ({ data: {} })),
        axios
          .get<RegionData>(`${BASE_URL}/${endpoint}/?${prevQueryParams}`, { headers })
          .catch(() => ({ data: {} })),
      ])
    );

    const currentResults = regionEndpoints.reduce((acc, { key }, idx) => {
      acc[key] = responses[idx * 2].data;
      return acc;
    }, {} as Record<string, RegionData>);

    const previousResults = regionEndpoints.reduce((acc, { key }, idx) => {
      acc[key] = responses[idx * 2 + 1].data;
      return acc;
    }, {} as Record<string, RegionData>);

    setData(currentResults);
    setPreviousData(previousResults);
    setLoading(false);
  };


  useEffect(() => {
    fetchCities();
    fetchEviction(selectedYear, selectedCity?.value || null);
    fetchRegionData(selectedYear, selectedCity?.value || null)
  }, [selectedYear, selectedCity]);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(event.target.value);
  };

  const handleCityChange = (option: CityOption | null) => {
    setSelectedCity(option);
  };

  const calculateChange = (current?: number, previous?: number): string => {
    if (previous == null || previous === 0) return "0.0"; // prevent divide by zero
    const change = (((current ?? 0) - previous) / previous) * 100;
    return change.toFixed(1);
  };

  const chartIds: Record<string, string> = {
    byTenureType: "my-chart-id-2",
    byEntity: "my-chart-id-5",
    byAgreementType: "my-chart-id-6",
    byPropertyType: "my-chart-id-7",
  };

  useEffect(() => {
    const fetchPartnerLogos = async () => {
      try {
        const response = await fetch(`${BASE_URL}/partners?category=data_partners`);
        const data = await response.json();
        setPartnerLogos(data);
      } catch (error) {
        console.error("Failed to fetch partner logos:", error);
      }
    };

    fetchPartnerLogos();
  }, []);

  const fmt = (n?: number) => (n ? n.toLocaleString() : "0");

  const loadInfrastructureData = async (year: string, city: string | null) => {
    try {
      setLoading(true);
      setError(null);

      // Same query params style you already use
      const queryParams = `year=${year}${city ? `&city=${city.toLocaleLowerCase()}` : ""}`;

      const [communityRes, humanitarianRes] = await Promise.all([
        fetch(`${BASE_URL}/infrastructure/community/?${queryParams}`),
        fetch(`${BASE_URL}/infrastructure/humanitarian/?${queryParams}`),
      ]);

      if (!communityRes.ok || !humanitarianRes.ok) {
        throw new Error("Failed to fetch infrastructure data");
      }

      const community = await communityRes.json();
      const humanitarian = await humanitarianRes.json();

      setCommunityData(community);
      setHumanitarianData(humanitarian);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInfrastructureData(selectedYear, selectedCity?.value || null);
  }, [selectedYear, selectedCity]); // re-fetch when filters change


  return (
    <div className="w-full">
      <p className="text-3xl font-bold mb-4">Evictions</p>
      <div className="grid grid-cols-1 gap-4 rounded">
        <div className="md:col-span-2 sm:col-span-1">
          <div className="border border-dark rounded bg-dark text-white py-2 px-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
              {/* Year Select */}
              <div className="flex items-center gap-2">
                <label htmlFor="year-select">Select Year:</label>
                <div style={{ minWidth: "200px" }}>
                  <select
                    id="year-select"
                    value={selectedYear || ""}
                    onChange={handleYearChange}
                    className="w-full rounded px-2 py-[6px] bg-white text-[#4B5563] border border-[#D1D5DB]"
                  >
                    <option value="">Cumulative Period</option>
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Region Select */}
              <div className="flex items-center gap-2">
                <label htmlFor="city-select">Select Regions:</label>
                <div style={{ minWidth: "200px" }}>
                  <Select
                    id="city-select"
                    options={cities}
                    value={selectedCity}
                    onChange={handleCityChange}
                    placeholder="All Regions"
                    isClearable
                    isSearchable
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                        color: "gray",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 sm:col-span-1 w-full min-w-0">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold whitespace-nowrap shrink-0
        ${tab === 1 ? "border-b-2 border-orange" : ""}`}
              onClick={() => setTab(1)}
            >
              Trends
            </p>
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold whitespace-nowrap shrink-0
        ${tab === 2 ? "border-b-2 border-orange" : ""}`}
              onClick={() => setTab(2)}
            >
              Incidences
            </p>
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold whitespace-nowrap shrink-0
        ${tab === 3 ? "border-b-2 border-orange" : ""}`}
              onClick={() => setTab(3)}
            >
              Infrastructure Destroyed
            </p>
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold whitespace-nowrap shrink-0
        ${tab === 4 ? "border-b-2 border-orange" : ""}`}
              onClick={() => setTab(4)}
            >
              Protection Threats and Risks
            </p>
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold whitespace-nowrap shrink-0
        ${tab === 5 ? "border-b-2 border-orange" : ""}`}
              onClick={() => setTab(5)}
            >
              Data Contributing Partners
            </p>
          </div>
        </div>

        {tab === 1 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">
            <div className="shadow-lg p-6 rounded mb-4">
              <p className="text-lg font-bold">Eviction Trends By Month</p>
              <EvictionByMonth
                selectedYear={selectedYear}
                selectedCity={selectedCity?.value || null}
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#1f2937]">
                Summary for {selectedCity?.label || "All Regions"}{" "}
                {selectedYear ? `in ${selectedYear}` : " (All Time)"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <DataRowProp
                  label="Protected Individuals"
                  icon={<Users size={20} color="#16a34a" />}
                  current={data.protectedIndividuals?.total_individuals}
                  previous={previousData.protectedIndividuals?.total_individuals}
                  pctChange={calculateChange}
                  fmt={fmt}
                />
                <DataRowProp
                  label="Protected Households"
                  icon={<Home size={20} color="#16a34a" />}
                  current={data.protectedHouseholds?.total_households}
                  previous={previousData.protectedHouseholds?.total_households}
                  pctChange={calculateChange}
                  fmt={fmt}
                />

                <DataRowProp
                  label="Evicted Individuals"
                  icon={<Users size={20} color="#dc2626" />}
                  current={data.evictedIndividuals?.total_individuals}
                  previous={previousData.evictedIndividuals?.total_individuals}
                  pctChange={calculateChange}
                  fmt={fmt}
                />

                <DataRowProp
                  label="Evicted Households"
                  icon={<Home size={20} color="#dc2626" />}
                  current={data.evictedHouseholds?.total_households}
                  previous={previousData.evictedHouseholds?.total_households}
                  pctChange={calculateChange}
                  fmt={fmt}
                />
              </div>
            </div>
          </div>
        )}

        {tab === 2 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">
            {evictionTypes.map(({ key, label }) => (
              <div key={key} className="shadow-lg p-6 rounded mb-4">
                <p className="text-lg font-bold">Incidences By {label}</p>
                <AnnualEvictionTrends
                  chartData={chartData[key] || { labels: [], datasets: [] }}
                  selectedYear={selectedYear}
                  selectedCity={selectedCity}
                  isLoading={isLoading}
                  id={chartIds[key] || ""}
                />
              </div>
            ))}
          </div>
        )}
        {tab === 3 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">
            <div className="shadow-lg p-6 rounded mb-4">
              <p className="text-lg font-bold">Destroyed Community Infrastructure</p>
              <InfrastructureBarChart
                id="community-chart"
                type="community"
                data={communityData}
                year={selectedYear}
                region={selectedCity?.value ?? null}
                isLoading={loading}
              />
            </div>
            <div className="shadow-lg p-6 rounded mb-4">
              <p className="text-lg font-bold">Destroyed Humanitarian Infrastructure</p>
              <InfrastructureBarChart
                id="community-chart"
                type="humanitarian"
                data={humanitarianData}
                year={selectedYear}
                region={selectedCity?.value ?? null}
                isLoading={loading}
              />
            </div>
          </div>
        )}

        {tab === 4 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">
            <div className="shadow-lg p-6 rounded mb-4">
              <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', padding: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#444' }}>Threats Data Table</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Protection Threat/Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { id: 1, threat: "Arbitrary or Unlawful evictions (at night etc)", count: 5000 },
                        { id: 2, threat: "Violence and Physical Harm", count: 1500 },
                        { id: 3, threat: "Destruction of Property and Basic needs", count: 1000 },
                        { id: 4, threat: "Increased risk of exploitation", count: 500 },
                        { id: 5, threat: "Disruption of Access to lifesaving/Basic services", count: 350 },
                        { id: 6, threat: "Family separation", count: 200 },
                        { id: 7, threat: "Psychosocial Impact", count: 150 },
                        { id: 8, threat: "Increases Risk of Re-victimization including exploitation", count: 100 },
                        { id: 9, threat: "Threats of Livelihood", count: 50 },
                        { id: 10, threat: "Displacement Hazards (Accidents, exposure to dangerous terrains)", count: 35 },
                        { id: 11, threat: "Others", count: 20 }
                      ].map((item) => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}>{item.id}</td>
                          <td style={{ padding: '12px' }}>{item.threat}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="shadow-lg p-6 rounded mb-4">
              <ProtectionRisksChart />
            </div>
            <div className="shadow-lg p-6 rounded mb-4">

            </div>
          </div>
        )}
        {tab === 5 && (
          <div className="md:col-span-2 sm:col-span-1">
            <div className="shadow-lg p-6 rounded mb-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {partnerLogos.map((file) => (
                  <div
                    key={file.id}
                    className="p-2 bg-white rounded shadow-sm flex items-center justify-center"
                  >
                    <img
                      src={file.thumbnail_url}
                      alt={file.title || "Partner logo"}
                      className="h-16 object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Evictions;